import { useEffect, useState } from 'react'

export type QuadrantKey =
  | 'highTrafficHighConversion'
  | 'highTrafficLowConversion'
  | 'lowTrafficHighConversion'
  | 'lowTrafficLowConversion'

export interface QuadrantItem {
  spuId: string
  name: string
  visitors: number
  customerCount: number
  conversionRate: number
  gmv: number
  soldItemCount: number
  quadrant: QuadrantKey
}

export interface P3ActionItem extends QuadrantItem {
  actionType: 'conversion' | 'traffic'
  action: string
}

export interface P3ReadySnapshot {
  status: 'ready'
  windowStart: string
  windowEnd: string
  dates: string[]
  eligibleSpuCount: number
  excludedLowVisitorCount: number
  medianVisitors: number
  medianConversionRate: number
  quadrants: Record<QuadrantKey, QuadrantItem[]>
  actionItems: P3ActionItem[]
}

export interface P3InsufficientSnapshot {
  status: 'insufficient'
  windowStart: string
  windowEnd: string
  dates: string[]
  missingDates: string[]
}

export type P3Snapshot = P3ReadySnapshot | P3InsufficientSnapshot

export interface FlowStats {
  visitors: number
  customerCount: number
  gmv: number
  conversionRate: number | null
}

export interface FlowChannelItem {
  channel: string
  scope: '站内' | '站外'
  hierarchyDepth: number
  current: FlowStats
  previous: FlowStats
  visitorDelta: number
  visitorChangeRate: number | null
  gmvDelta: number
  gmvChangeRate: number | null
  declineContributionRate: number
}

export interface FlowActionItem extends FlowChannelItem {
  action: string
}

export interface P1ReadySnapshot {
  status: 'ready'
  dataThrough: string
  currentWindow: { start: string; end: string; dates: string[] }
  previousWindow: { start: string; end: string; dates: string[] }
  totals: {
    current: FlowStats
    previous: FlowStats
    visitorDelta: number
    visitorChangeRate: number | null
    gmvDelta: number
    gmvChangeRate: number | null
    conversionRatePoint: number | null
  }
  negativeVisitorDeltaTotal: number
  channels: FlowChannelItem[]
  actionItems: FlowActionItem[]
}

export interface P1InsufficientSnapshot {
  status: 'insufficient'
  dataThrough: string | null
  currentDates: string[]
  previousDates: string[]
  missingDates: string[]
}

export type P1Snapshot = P1ReadySnapshot | P1InsufficientSnapshot

export interface AdPeriodSummary {
  periodLabel: string
  start: string
  end: string
  spend: number
  attributedGmv: number
  roi: number | null
}

export interface AdSearchCandidate {
  searchTerm: string
  campaignId: string
  campaignName: string
  unitId: string
  unitName: string
  skuId: string
  skuName: string
  spend: number
  clicks: number
  attributedGmv: number
}

export interface AdCampaignCandidateGroup {
  campaignId: string
  campaignName: string
  spend: number
  clicks: number
  candidateCount: number
}

export interface AdProductCandidate {
  skuId: string
  skuName: string
  spend: number
  attributedGmv: number
  actualRoi: number | null
  marginStatus: 'ready' | 'unavailable'
  marginReason: string | null
  profitWithRebateMargin: number | null
  breakEvenRoi: number | null
  roiGap?: number
}

export interface AdDiagnosticAction {
  kind: 'campaign' | 'search-term' | 'sku'
  objectId: string
  objectName: string
  evidence: string
  action: string
  indicators: string
  review: string
}

export interface P2Snapshot {
  status: 'ready'
  currentWindow: { start: string; end: string }
  previousWindow: { start: string; end: string }
  searchTerms: {
    current: AdPeriodSummary
    previous: AdPeriodSummary
    spendDelta: number
    attributedGmvDelta: number
    zeroAttributionSpend: number
    zeroAttributionClicks: number
    zeroCandidateCount: number
    candidates: AdSearchCandidate[]
    campaignGroups: AdCampaignCandidateGroup[]
  }
  products: {
    current: AdPeriodSummary
    previous: AdPeriodSummary
    spendDelta: number
    attributedGmvDelta: number
    zeroAttribution: AdProductCandidate[]
    lowEfficiency: AdProductCandidate[]
    marginCoverage: {
      adSkuCount: number
      matchedSkuCount: number
      eligibleSkuCount: number
      unavailableSkuCount: number
      sourceWindowStart: string
      sourceWindowEnd: string
      sourceProductCount: number
    }
  }
  actionItems: AdDiagnosticAction[]
}

export interface RefundStats {
  gmv: number
  refundAmount: number
  refundItemCount: number
  refundAmountShare: number | null
}

export interface RefundLevelSummary {
  gmv: number
  refundAmount: number
  refundAmountShare: number | null
  productCount: number
  zeroGmvRefundCount: number
  anomalyCount: number
}

export interface RefundWindowReady {
  status: 'ready'
  windowStart: string
  windowEnd: string
  dates: string[]
  levels: Record<'spu' | 'sku', RefundLevelSummary>
}

export interface RefundWindowInsufficient {
  status: 'insufficient'
  windowStart: string
  windowEnd: string
  dates: string[]
  missingDates: string[]
}

export type RefundWindow = RefundWindowReady | RefundWindowInsufficient

export interface RefundRiskItem {
  level: 'spu' | 'sku'
  productId: string
  name: string
  isSevenDayAnomaly: boolean
  isThirtyDayAnomaly: boolean
  sevenDay: RefundStats | null
  thirtyDay: RefundStats | null
}

export interface RefundActionItem extends RefundRiskItem {
  action: string
}

export interface P5Snapshot {
  sevenDay: RefundWindow
  thirtyDay: RefundWindow
  spuRisks: RefundRiskItem[]
  skuRisks: RefundRiskItem[]
  dailyTrend: Array<{
    date: string
    gmv: number
    refundAmount: number
    refundAmountShare: number | null
  }>
  actionItems: RefundActionItem[]
}

export interface AnalysisExpansionData {
  schemaVersion: string
  generatedAt: string
  meta: {
    minDate: string
    maxDate: string
    dedupe: string
  }
  p1: {
    methodology: {
      lookbackDays: number
      sourceScope: string
      hierarchyRule: string
      totalRule: string
      actionRule: string
      rawRowCount: number
      uniqueRowCount: number
      dedupe: string
    }
    latest: P1Snapshot
  }
  p2: {
    methodology: {
      attribution: string
      separationRule: string
      searchAggregation: string
      searchCandidateLimit: number
      productSpendThreshold: number
      breakEvenRule: string
      productCandidateLimit: number
      runSelection: string
      safety: string
    }
    latest: P2Snapshot
  }
  p3: {
    methodology: {
      lookbackDays: number
      minVisitors: number
      scope: string
      safety: string
    }
    byDate: Record<string, P3Snapshot>
  }
  p5: {
    methodology: {
      windows: number[]
      shareDefinition: string
      storeBaseline: string
      anomalyRule: string
      zeroGmvRule: string
    }
    byDate: Record<string, P5Snapshot>
  }
}

let analysisDataPromise: Promise<AnalysisExpansionData> | null = null

function fetchAnalysisData() {
  if (!analysisDataPromise) {
    analysisDataPromise = fetch('/data/analysis-data.json', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`分析扩展数据加载失败：${response.status}`)
        return response.json() as Promise<AnalysisExpansionData>
      })
      .catch((error) => {
        analysisDataPromise = null
        throw error
      })
  }
  return analysisDataPromise
}

export function useAnalysisExpansionData() {
  const [data, setData] = useState<AnalysisExpansionData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    fetchAnalysisData()
      .then((payload) => {
        if (active) setData(payload)
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : '分析扩展数据加载失败')
      })
    return () => {
      active = false
    }
  }, [])

  return { data, error, loading: !data && !error }
}
